import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Search, X } from 'lucide-react';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { egyptianLocations } from '@/data/locations';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface CitySelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  language?: 'en' | 'ar';
}

const CitySelectionPopup: React.FC<CitySelectionPopupProps> = ({ 
  isOpen, 
  onClose, 
  language = 'en' 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Remove language argument from useCityNavigation and all usages
  const { setSelectedCity } = useDeliveryLocation();
  const navigate = useNavigate();
  // Use language only for translations and city name display

  const translations = {
    en: {
      title: "Select Your Delivery Location",
      subtitle: "Choose your city to see available products and delivery options",
      searchPlaceholder: "Search for your city...",
      popularCities: "Popular Cities",
      allCities: "All Cities",
      selectCity: "Select City",
      close: "Close",
      skipForNow: "Skip for now"
    },
    ar: {
      title: "اختر موقع التوصيل",
      subtitle: "اختر مدينتك لرؤية المنتجات المتاحة وخيارات التوصيل",
      searchPlaceholder: "ابحث عن مدينتك...",
      popularCities: "المدن الشائعة",
      allCities: "جميع المدن",
      selectCity: "اختر المدينة",
      close: "إغلاق",
      skipForNow: "تخطى الآن"
    }
  };

  // Fetch cities from database (Egypt only)
  const { data: dbCities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cities')
        .select('name_en, name_ar, slug, url_path, countries!inner (country_code)')
        .eq('is_active', true)
        .eq('countries.country_code', 'eg')
        .order('name_en');
      if (error) throw error;
      return data;
    }
  });

  const t = translations[language];

  // Helper to get city name in correct language
  const getCityName = (city: any) => {
    if (language === 'ar' && city.name_ar) return city.name_ar;
    return city.name_en || city.name;
  };

  // Use dbCities if available, fallback to static
  const allCities = dbCities.length > 0 ? dbCities : egyptianLocations;
  const popularCities = allCities.slice(0, 6);
  const filteredCities = allCities.filter(city =>
    getCityName(city).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCitySelect = (city: any) => {
    setSelectedCity(city);
    // Build the correct language-prefixed path
    const langPrefix = language === 'ar' ? 'ar-eg' : 'en-eg';
    const cityPath = `/${langPrefix}/${city.slug}`;
    navigate(cityPath);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            {t.title}
          </DialogTitle>
          <DialogDescription>
            {t.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Popular Cities */}
          {!searchTerm && (
            <div>
              <Label className="text-sm font-medium text-foreground mb-2 block">
                {t.popularCities}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {popularCities.map((city) => (
                  <Button
                    key={city.slug}
                    variant="outline"
                    className="justify-start h-auto p-3"
                    onClick={() => handleCitySelect(city)}
                  >
                    <MapPin className="h-4 w-4 mr-2 text-primary" />
                    <div className="text-left">
                      <div className="font-medium">{getCityName(city)}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* All Cities or Search Results */}
          <div>
            <Label className="text-sm font-medium text-foreground mb-2 block">
              {searchTerm ? 'Search Results' : t.allCities}
            </Label>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {filteredCities.map((city) => (
                <Button
                  key={city.slug}
                  variant="ghost"
                  className="w-full justify-start h-auto p-3"
                  onClick={() => handleCitySelect(city)}
                >
                  <MapPin className="h-4 w-4 mr-2 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">{getCityName(city)}</div>
                  </div>
                </Button>
              ))}
              {searchTerm && filteredCities.length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                  No cities found matching "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="ghost" onClick={onClose}>
            {t.skipForNow}
          </Button>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            {t.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CitySelectionPopup; 